import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BrandHome } from "@/components/BrandHome";
import {
  UserPlus, FolderPlus, Target, Wand2, Pencil, Download, CalendarClock, LineChart,
  ArrowLeft, PlayCircle, Film, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import SiteFooter from "@/components/SiteFooter";
import ScrollToTop from "@/components/ScrollToTop";

const IMG = (id) => `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200`;

const STEPS = [
  {
    icon: UserPlus, tag: "01",
    title: "أنشئ حسابك في دقيقة",
    desc: "ابدأ بإنشاء حساب بالبريد الإلكتروني أو عبر Google. حسابك هو بوابتك لكل مشاريعك واستراتيجياتك ومحتواك في مكان واحد.",
    bullets: ["تسجيل سريع بالبريد أو Google", "لوحة تحكم عربية بالكامل", "كل مشاريعك محفوظة ومنظّمة"],
    image: IMG("photo-1556155092-490a1ba16284"),
  },
  {
    icon: FolderPlus, tag: "02",
    title: "أنشئ مشروعك وارفع شعارك",
    desc: "أضف اسم علامتك التجارية ومجال نشاطك وارفع شعارك. رواج الذكية يستخدم هذه المعلومات لتخصيص كل ما يُولَّد لاحقاً بهويتك.",
    bullets: ["اسم العلامة والمجال والجمهور", "رفع الشعار لدمجه تلقائياً على المحتوى", "ثلاثة مسارات: محتوى فردي، مشروع محتوى، استراتيجية كاملة"],
    image: IMG("photo-1625236466493-72842ceec47b"),
  },
  {
    icon: Target, tag: "03",
    title: "ولّد استراتيجيتك التسويقية",
    desc: "بضغطة واحدة يبني رواج الذكية تحليل SWOT، ودراسة السوق والجمهور المستهدف، والهوية البصرية — أساس متين قبل إنتاج أي محتوى.",
    bullets: ["تحليل SWOT ونقاط التميّز", "تحليل السوق والجمهور المثالي", "هوية بصرية ونبرة صوت موحّدة"],
    image: IMG("photo-1676276375742-9e3d10e39d45"),
  },
  {
    icon: Wand2, tag: "04",
    title: "أنتج المحتوى من استوديو رواج الذكية",
    desc: "تدفّق موجّه ذكي: يُولَّد النص والسكربت أولاً، توافق عليه، ثم تُولَّد الصورة أو الفيديو — بهوية سعودية أصيلة وبأبعاد القناة الصحيحة.",
    bullets: ["نص/سكربت أولاً ثم موافقتك ثم الوسائط", "صور وفيديو بهوية سعودية دقيقة", "أبعاد جاهزة لكل منصة (مربّع/عمودي)"],
    image: IMG("photo-1676282827533-d6058df56a69"),
  },
  {
    icon: Pencil, tag: "05",
    title: "حرّر وأضف الشعار والنصوص",
    desc: "قبل التنزيل، افتح محرّر الصور لإضافة الشعار والعناوين والنصوص وتحديد موضعها وحجمها ولونها بما يناسب علامتك.",
    bullets: ["إضافة الشعار والنص فوق الصورة", "تحكّم بالموضع والحجم واللون", "نصوص عربية بمحاذاة صحيحة"],
    image: IMG("photo-1562577309-4932fdd64cd1"),
  },
  {
    icon: Download, tag: "06",
    title: "احفظ للاستوديو أو نزّل",
    desc: "بضغطة تنزيل واحدة: على الجوال تُحفظ الصورة/الفيديو مباشرة في استوديو الصور (المعرض)، وعلى الكمبيوتر تُنزَّل بالشكل الاعتيادي.",
    bullets: ["حفظ مباشر لمعرض صور الجوال", "تنزيل عادي على الكمبيوتر", "نسخ النص والهاشتاقات الجاهزة"],
    image: IMG("photo-1622532832487-b18b9c7f23d3"),
  },
  {
    icon: CalendarClock, tag: "07",
    title: "أطلق حملتك الشهرية",
    desc: "أنشئ حملة من 14 صورة + فيديوين، مع نصوص وهاشتاقات تُولَّد تلقائياً لكل منشور، وجدولة ذكية بأفضل أوقات النشر للسوق السعودي.",
    bullets: ["نصوص وهاشتاقات تلقائية لكل منشور", "جدولة بأفضل الأوقات تلقائياً", "تذكير بالنشر وتعليم «تم الرفع»"],
    image: IMG("photo-1601342630314-8427c38bf5e6"),
  },
  {
    icon: LineChart, tag: "08",
    title: "انشر وتابع الأداء وحسّن",
    desc: "ارفع المحتوى على قنواتك، علّم «تم الرفع»، ثم تابع الأداء وأدخِل بيانات التفاعل لتحصل على توصيات تحسين مستمرة.",
    bullets: ["متابعة أداء كل منشور", "توصيات تحسين أسبوعية", "قرارات مبنية على الأرقام لا التخمين"],
    image: IMG("photo-1599658880436-c61792e70672"),
  },
];

function Chrome({ children }) {
  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <header className="border-b border-border sticky top-0 z-30 bg-background/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandHome />
          <nav className="flex items-center gap-5 text-sm">
            <Link to="/blog" className="text-muted-foreground hover:text-foreground transition" data-testid="guide-nav-blog">المدونة</Link>
            <Link to="/register"><Button size="sm" className="bg-primary hover:bg-primary/90 text-white">ابدأ الآن</Button></Link>
          </nav>
        </div>
      </header>
      {children}
      <SiteFooter />
      <ScrollToTop />
    </div>
  );
}

function VideoEmbed({ url, landscape }) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  if (yt) {
    return (
      <div className="aspect-video w-full rounded-xl overflow-hidden border border-border">
        <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${yt[1]}`} title="فيديو إرشادي" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      </div>
    );
  }
  if (landscape) return <video className="w-full aspect-video bg-black object-contain" src={url} controls playsInline preload="metadata" data-testid="guide-intro-video" />;
  return <video className="w-full aspect-[9/16] max-h-[520px] bg-black object-contain" src={url} controls playsInline preload="metadata" />;
}

function GuideVideos() {
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    api.get("/blog/posts?type=guide").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  return (
    <section id="videos" className="max-w-6xl mx-auto px-6 mt-24 scroll-mt-24" data-testid="guide-videos-section">
      <div className="flex items-center gap-3 mb-2">
        <Film className="w-6 h-6 text-primary" />
        <h2 className="text-2xl sm:text-3xl font-extrabold">فيديوهات إرشادية</h2>
      </div>
      <p className="text-muted-foreground mb-8">شروحات بالفيديو لاستخدام المنصة خطوة بخطوة بذكاء.</p>

      {posts === null ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => <div key={i} className="h-64 rounded-2xl bg-secondary/40 animate-pulse" />)}
        </div>
      ) : posts.length === 0 ? (
        <div className="glass rounded-2xl border border-border p-10 text-center" data-testid="guide-videos-empty">
          <PlayCircle className="w-12 h-12 text-primary/60 mx-auto mb-4" />
          <h3 className="text-lg font-bold mb-2">الفيديوهات الإرشادية قيد الإضافة</h3>
          <p className="text-muted-foreground text-sm max-w-md mx-auto">نعمل على تجهيز مكتبة فيديوهات تشرح كل ميزة بالتفصيل. حتى ذلك الحين، تابع الخطوات المصوّرة بالأعلى — فهي تغطي المنصة من الألف إلى الياء.</p>
          <Link to="/register"><Button className="mt-6 bg-primary hover:bg-primary/90 text-white">جرّب المنصة الآن</Button></Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...posts].sort((a, b) => (a.order || 0) - (b.order || 0)).map((p) => {
            const vid = (p.media || []).find((m) => m.type === "video");
            const vurl = vid ? (vid.video_id ? `${process.env.REACT_APP_BACKEND_URL}/api/guide-video/${vid.video_id}` : vid.url) : null;
            const landscape = !!(vid && vid.landscape);
            return (
              <div key={p.slug} className={`glass rounded-2xl border border-border overflow-hidden flex flex-col ${landscape ? "sm:col-span-2 lg:col-span-3" : ""}`} data-testid={`guide-video-${p.slug}`}>
                {vurl ? <VideoEmbed url={vurl} landscape={landscape} />
                  : p.cover_image ? <img src={p.cover_image} alt={p.title} className="w-full aspect-video object-cover" />
                  : <div className="w-full aspect-video bg-secondary/40 flex items-center justify-center"><PlayCircle className="w-10 h-10 text-primary/50" /></div>}
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-bold mb-1.5">{p.title}</h3>
                  {p.excerpt && <p className="text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default function Guide() {
  return (
    <Chrome>
      <Seo
        title="دليل استخدام المنصة من الألف إلى الياء"
        description="تعلّم كيف تستخدم رواج الذكية خطوة بخطوة: إنشاء المشروع، توليد الاستراتيجية، إنتاج الصور والفيديو والنصوص، إطلاق الحملة الشهرية، والنشر ومتابعة الأداء — بالصور وفيديوهات إرشادية."
        keywords="شرح رواج الذكية, دليل استخدام منصة تسويق, كيف أستخدم رواج الذكية, فيديوهات إرشادية تسويق, توليد محتوى بالذكاء الاصطناعي"
        path="/guide"
      />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-6 text-center">
        <span className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20 mb-5">
          <Sparkles className="w-3.5 h-3.5" /> دليل الاستخدام الكامل
        </span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-5">
          استخدم رواج الذكية من <span className="text-primary">الألف إلى الياء</span> بذكاء
        </h1>
        <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
          رحلة مصوّرة خطوة بخطوة — من إنشاء حسابك وبناء استراتيجيتك، إلى إنتاج المحتوى وإطلاق حملتك ومتابعة نتائجها.
        </p>
      </section>

      {/* Steps */}
      <section className="max-w-6xl mx-auto px-6 mt-10 space-y-16 sm:space-y-24" data-testid="guide-steps">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const reversed = i % 2 === 1;
          return (
            <div key={s.tag} className={`grid lg:grid-cols-2 gap-8 lg:gap-14 items-center ${reversed ? "lg:[direction:ltr]" : ""}`} data-testid={`guide-step-${s.tag}`}>
              <div className={reversed ? "lg:[direction:rtl]" : ""}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary"><Icon className="w-6 h-6" /></span>
                  <span className="text-5xl font-black text-primary/15">{s.tag}</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold mb-3">{s.title}</h2>
                <p className="text-muted-foreground leading-loose mb-5">{s.desc}</p>
                <ul className="space-y-2.5">
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-foreground/90">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={reversed ? "lg:[direction:rtl]" : ""}>
                <div className="rounded-3xl overflow-hidden border border-border shadow-2xl shadow-primary/5">
                  <img src={s.image} alt={s.title} loading="lazy" className="w-full h-full object-cover aspect-[4/3]" />
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* Instructional videos */}
      <GuideVideos />

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 mt-24">
        <div className="glass rounded-3xl border border-primary/20 p-10 text-center bg-primary/5">
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-3">جاهز تبدأ تسويقك باحتراف؟</h2>
          <p className="text-muted-foreground mb-6">أنشئ حسابك وابدأ أول استراتيجية ومحتوى خلال دقائق.</p>
          <Link to="/register"><Button size="lg" className="bg-primary hover:bg-primary/90 text-white gap-2" data-testid="guide-cta-register">ابدأ الآن مجاناً <ArrowLeft className="w-4 h-4" /></Button></Link>
        </div>
      </section>
    </Chrome>
  );
}
